import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../src/environments/environment';
import {ReactiveServiceWithFilters} from '../reactive-service-with-filters.abstract';
import {ParamlistModel} from './paramlist.model';
import {of} from 'rxjs/internal/observable/of';
import {LocalStorageService} from '../locale-storage/locale-storage.service';

const PARAM_LIST_SERVICE_MAX_ROWS_LIMIT = 1000;

@Injectable()
export class ParamlistsApiService extends ReactiveServiceWithFilters  {
  protected baseUrl = environment.backEndUrlPrefix + '/' + 'db/contrdeviceparam';
  protected rootUrl = environment.backEndUrlPrefix + '/' + 'search/params';

  private selectedParams: ParamlistModel[];
  protected arrayFieldsNames = ['oid', 'procId', 'rubricId', 'tags', 'valueTypId', 'zones', 'zoneId'];

  setSelectedParams(data: ParamlistModel[]) {
    this.selectedParams = data;
  }

  getSelectedParams(): ParamlistModel[] {
    return this.selectedParams;
  }

  private selectedParamsSubject = new BehaviorSubject<ParamlistModel[]>([]);

  selectedParams$ = this.selectedParamsSubject.asObservable();

  public updateSelectedParams(params: ParamlistModel[]) {
    this.selectedParamsSubject.next(params);
  }

  constructor(protected localStorageApi: LocalStorageService, protected http: HttpClient) {
    super();
  }

  getAll(limit: number, queryStr: any, httpMethod?: string) {
    let body: any = {};
    if (queryStr && httpMethod && httpMethod === 'GET') {
      const normalizedQuery = this.normalizeQuery(queryStr);
      const url = this.build2Url(normalizedQuery, limit);
      return this.http.get<ParamlistModel[]>(url);
    }
    if (queryStr) {
      const normalizedQuery = this.normalizeQuery(queryStr);
      body = this.queryStringToObject(normalizedQuery);
    } else {
      const oidQuery = this.localStorageApi.load(true, 'oidquerystring');
      const ridQuery = this.localStorageApi.load(true, 'ridquerystring');
      const hasOid = oidQuery && oidQuery !== '';
      const hasRid = ridQuery && ridQuery !== '';
      if (!hasOid && !hasRid) {
        return of(null);
      }
      const combinedQuery = [ridQuery, oidQuery].filter(Boolean).join('&');
      const normalized = this.normalizeQuery(combinedQuery);
      body = this.queryStringToObject(normalized);
    }

    if (limit !== undefined) {
      body.limit = limit;
    }
    return this.http.post<ParamlistModel[]>(this.rootUrl, body);
  }

  private queryStringToObject(query: string): any {
    const obj: any = {};
    if (!query) {
      return obj;
    }
    query.split('&').forEach(pair => {
      const [key, value] = pair.split('=');
      if (key && value !== undefined) {
        if (obj.hasOwnProperty(key)) {
          if (Array.isArray(obj[key])) {
            obj[key].push(value);
          } else {
            obj[key] = [obj[key], value];
          }
        } else {
          if (this.arrayFieldsNames.includes(key)) {
            if (!obj[key]) {
              obj[key] = [];
            }
            obj[key].push(value);
          } else {
            obj[key] = value;
          }
        }
      }
    });
    return obj;
  }

  private normalizeQuery(query: any): string {
    return query
      .replaceAll('oid=oid=', 'oid=')
      .replaceAll('rid=rid=', 'rid=')
      .replaceAll('rid=', 'rubricId=');
  }

  private build2Url(query: string, limit?: number): string {
    const queryParams = query ? `?${query}` : '';
    const limitParam = limit !== undefined ? `limit=${limit}` : '';
    const parts = [this.rootUrl, queryParams, limitParam].filter(Boolean);
    if (query && limit !== undefined) {
      return `${this.rootUrl}?${query}&limit=${limit}`;
    }
    return parts.join(limit && query ? '&' : '');
  }


  updateGroup(ids: number[], data): Observable<ParamlistModel[]> {
    return this.http.patch<ParamlistModel[]>(this.baseUrl, data, {params: {id: ids.map(id => id.toString())}});
  }

  deleteGroup(ids: number[]): Observable<string> {
    return this.http.delete<string>(this.baseUrl, {params: {id: ids.map(id => String(id))}});
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<ParamlistModel[]> | Promise<ParamlistModel[]> | ParamlistModel[] {
    this.setFilterParams(route);
    if (route.params.zoneid) {
      return this.getAll(PARAM_LIST_SERVICE_MAX_ROWS_LIMIT, `zoneId=${route.params.zoneid}`);
    }
    this.setQueryParams(route);
    const parts: string[] = [];
    const params = route.queryParams;
    if (params.oid) {
      const oids = Array.isArray(params.oid) ? params.oid : [params.oid];
      oids.forEach(o => parts.push(`oid=${o}`));
    }
    if (params.rid) {
      const rids = Array.isArray(params.rid) ? params.rid : [params.rid];
      rids.forEach(r => parts.push(`rid=${r}`));
    }
    const queryStr = parts.join('&');
    return this.getAll(PARAM_LIST_SERVICE_MAX_ROWS_LIMIT, queryStr || null);
  }

}

